import { setLocale } from '@/i18n';
import {
  SCREENSHOT_ACCOUNTS,
  SCREENSHOT_PASSWORD,
  isScreenshotEmail,
} from '@/services/screenshot-accounts.service';
import { slideByNumber } from '@/utils/screenshot-slides';
import { supabaseAuthService } from '@/services/supabase/auth.service';
import { dailyCompletionService } from '@/services/dailyCompletion.service';
import { supabase } from '@/lib/supabase';
import { useAppContextStore } from '@/stores/appContext';
import { eventBus } from '@/utils/eventBus';
import { useNotificationQueue, setNotificationSuppression, allowNextNotification } from '@/composables/useNotificationQueue';
import type { Router } from 'vue-router';

declare global {
  interface Window {
    /** Set by applyIntent when a screenshot slide has finished rendering.
     *  Format: "<lang>:<slide>", e.g. "en:8". Screenshot context only. */
    __shotReady?: string;
  }
}

const INTENT_KEY = 'harmonia_screenshot_intent';

interface ScreenshotIntent { lang: string; slide: number; }

function parseIntent(rawUrl: string): ScreenshotIntent | null {
  // Accept com.withharmonia.app://screenshot?lang=de&slide=4
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    url = new URL(rawUrl.replace('com.withharmonia.app://', 'https://harmonia.app/'));
  }
  const isScreenshot = rawUrl.includes('screenshot');
  if (!isScreenshot) return null;
  const lang = url.searchParams.get('lang');
  const slide = Number(url.searchParams.get('slide'));
  if (!lang || !slide || !slideByNumber(slide)) return null;
  return { lang, slide };
}

/** Entry point from App.vue's appUrlOpen. Returns true if it handled the URL. */
export async function handleScreenshotDeepLink(rawUrl: string, router: Router): Promise<boolean> {
  const intent = parseIntent(rawUrl);
  if (!intent) return false;

  const account = SCREENSHOT_ACCOUNTS.find(a => a.locale === intent.lang);
  if (!account) {
    console.log(`SHOT_ERROR:${intent.lang}:${intent.slide}:unknown-locale`);
    return true;
  }

  // Persist so the intent survives a login reload.
  try { localStorage.setItem(INTENT_KEY, JSON.stringify(intent)); } catch (_) { /* noop */ }

  const { data } = await supabase.auth.getUser();
  const currentEmail = data.user?.email ?? null;

  if (currentEmail !== account.email) {
    // Wrong (or no) account -> log into the target account. This reloads the app;
    // consumePendingScreenshotIntent() picks the intent back up on boot.
    try { localStorage.setItem('user-locale', account.locale); } catch (_) { /* noop */ }
    try { await supabaseAuthService.logout(); } catch (_) { /* noop */ }
    await supabaseAuthService.signInWithEmailPassword(account.email, SCREENSHOT_PASSWORD);
    window.location.href = '/tabs/tasks';
    return true;
  }

  // Already on the right account -> apply now.
  await applyIntent(intent, router);
  return true;
}

/** Called once on app boot. Applies a stored intent if the screenshot account is active. */
export async function consumePendingScreenshotIntent(router: Router): Promise<void> {
  let intent: ScreenshotIntent | null = null;
  try {
    const raw = localStorage.getItem(INTENT_KEY);
    if (raw) intent = JSON.parse(raw);
  } catch (_) { /* noop */ }
  if (!intent) return;

  // The session may be injected into native storage (NSUserDefaults) by the
  // screenshot runner rather than established via a login this launch, so on a
  // cold boot supabase.auth.getUser() can briefly return null while the session
  // is still being restored/refreshed from the storage adapter. Poll a few
  // seconds for the screenshot account to appear before giving up — otherwise
  // the intent is silently skipped and the slide never renders.
  const deadline = Date.now() + 8000;
  let isScreenshot = false;
  while (Date.now() < deadline) {
    const { data } = await supabase.auth.getUser();
    if (isScreenshotEmail(data.user?.email)) { isScreenshot = true; break; }
    await new Promise((r) => setTimeout(r, 300));
  }
  if (!isScreenshot) return; // only in screenshot context
  await applyIntent(intent, router);
}

async function applyIntent(intent: ScreenshotIntent, router: Router): Promise<void> {
  try { localStorage.removeItem(INTENT_KEY); } catch (_) { /* noop */ }

  // Reset readiness for this slide; set again only once this slide settles.
  try { window.__shotReady = undefined; } catch (_) { /* noop */ }
  try { localStorage.removeItem('__shot_ready'); } catch (_) { /* noop */ }

  // Block every notification modal (level-up, chest, admin…) for the rest of the
  // session so nothing pops over the captures; the reward slide explicitly
  // allows one through. Also clear any modal left over from a previous slide.
  setNotificationSuppression(true);
  try { useNotificationQueue().clearAll(); } catch (_) { /* noop */ }

  const slide = slideByNumber(intent.slide);
  if (!slide) { console.log(`SHOT_ERROR:${intent.lang}:${intent.slide}:bad-slide`); return; }

  // The app decides for itself which account it is on, and logs in if it is on
  // the wrong one. Returns true when a reload was started: everything below
  // belongs to the next boot.
  if (await ensureScreenshotAccount(intent)) return;

  // Set the active locale. setLocale loads the lazy locale chunk first, so the
  // same-account fast path (no reload) doesn't flash English fallback strings.
  try { await setLocale(intent.lang); } catch (_) { /* noop */ }

  // The household (and therefore isPremium + the children list) loads async
  // after auth. On a cold boot the deep link can arrive first, so wait for it —
  // otherwise child-mode slides get bounced to the dashboard by the
  // requiresPremium guard, and firstChildTasks finds no children.
  await waitForHouseholdReady();

  // For child-mode slides, ensure a child is selected and child view mode is on.
  if (slide.childMode) {
    const appContext = useAppContextStore();
    const child = appContext.currentChild ?? appContext.availableChildren[0];
    if (child) {
      if (!appContext.currentChild) appContext.setCurrentChild(child as any);
      appContext.enterChildMode(child.id);
    }
  }

  // The Tasks slide should show the FIRST child's page (not the "All" overview)
  // with at least 3 tasks ticked, so the capture reads as a real, in-progress day.
  let route = slide.route;
  if (slide.action === 'firstChildTasks') {
    const appContext = useAppContextStore();
    const firstChild = appContext.availableChildren[0] as { id: string } | undefined;
    if (firstChild) {
      const today = new Date().toISOString().split('T')[0];
      await ensureMinCompletedTasks(firstChild.id, today, 3);
      route = `${slide.route}?childId=${firstChild.id}`;
    }
  }

  // The avatarLobby slide renders the AvatarAlbum over the first child's Tasks
  // page, so it needs the ?childId= context too (the lobby reads the top-bar
  // child).
  if (slide.action === 'avatarLobby') {
    const appContext = useAppContextStore();
    const firstChild = appContext.availableChildren[0] as { id: string } | undefined;
    if (firstChild) {
      if (!appContext.currentChild) appContext.setCurrentChild(firstChild as any);
      route = `${slide.route}?childId=${firstChild.id}`;
    }
  }

  // The Habits slide shows the first child's anchoring statuses. Its history is
  // NOT seeded here: a daily habit needs 14 occurrences over 21 days before it
  // leaves "Nouvelle", which is weeks of daily_completions rows — written once,
  // out of band, by scripts/screenshots-fixtures.ts. All this slide does is put
  // the right child in context; the scroll happens after the route lands.
  if (slide.action === 'habitsList') {
    const appContext = useAppContextStore();
    const firstChild = appContext.availableChildren[0] as { id: string } | undefined;
    if (firstChild) {
      if (!appContext.currentChild) appContext.setCurrentChild(firstChild as any);
      route = `${slide.route}?childId=${firstChild.id}`;
    }
  }

  // The Rewards slide shows the first child's curated path: normalize it right
  // before rendering (points drift from task completions made by other slides
  // can auto-collect a station and break the composition).
  if (slide.action === 'rewardsPath') {
    const appContext = useAppContextStore();
    const firstChild = appContext.availableChildren[0] as { id: string; points?: number } | undefined;
    if (firstChild) {
      if (!appContext.currentChild) appContext.setCurrentChild(firstChild as any);
      await normalizeRewardPath(firstChild);
    }
  }

  // The Allowance slide opens the "Argent de poche" panel (?tab=allowance on the
  // rewards page). Preload the balance + movements for the first child so the
  // panel renders populated rather than catching its own onMounted fetch mid-load.
  if (slide.action === 'allowanceTab') {
    const appContext = useAppContextStore();
    const firstChild = appContext.availableChildren[0] as { id: string } | undefined;
    if (firstChild) {
      if (!appContext.currentChild) appContext.setCurrentChild(firstChild as any);
      await preloadAllowance(firstChild.id);
    }
  }

  await router.replace(route);

  // Celebration slides: fire the modal over the route just rendered.
  if (slide.action === 'chest') await triggerCelebration('chest');
  if (slide.action === 'reward') await triggerCelebration('reward');

  // Habits slide: no scrolling, the frame starts at the top of the page. Just
  // let the history land before the ready signal goes out.
  if (slide.action === 'habitsList') await waitForHabitsContent();

  // Overlay slide: the hosting page registers its eventBus listener onMounted,
  // which happens asynchronously after router.replace — emit a few times over
  // ~2s (opening is idempotent) so the modal reliably ends up open.
  if (slide.action === 'avatarLobby') {
    for (const delay of [500, 1200, 2200]) {
      setTimeout(() => { try { eventBus.emit('app:screenshot-open-avatar-lobby', undefined); } catch (_) { /* noop */ } }, delay);
    }
  }

  // Hide the screenshot dev panel so it doesn't pollute the capture.
  window.dispatchEvent(new CustomEvent('screenshot:hide-panel'));

  // Signal readiness after the next paint + a short settle for data/anim.
  // Also persist the signal to localStorage so a headless capture runner can
  // poll it (the simulator can read the WebView's localStorage SQLite, but not
  // window.* or console.log). The runner waits for SHOT_READY_KEY to equal
  // "<lang>:<slide>" before capturing, so it never grabs the boot splash.
  requestAnimationFrame(() => {
    setTimeout(() => {
      const key = `${intent!.lang}:${intent!.slide}`;
      window.__shotReady = key;
      try { localStorage.setItem('__shot_ready', key); } catch (_) { /* noop */ }
      console.log(`SHOT_READY:${key}`);
    }, 1200);
  });
}

/**
 * Make sure the app is signed in as the account for THIS capture's language,
 * and log in if it is not. Returns true when a reload was started.
 *
 * Why the app checks instead of trusting what was injected: the runner writes
 * the session into the app's native preferences from OUTSIDE the app, and that
 * write is not reliably what the app reads back. Measured on the simulator, at
 * length: `defaults read` returned the French session, the app booted on the
 * Ukrainian household, made NO auth request at all (the local GoTrue log shows
 * none), and wrote the Ukrainian profile to its cache — an access token from a
 * previous account, still valid for the hour, served to the app from a cache
 * we cannot reach. Deleting the preferences domain, wiping the whole WebKit
 * directory and killing `cfprefsd` each changed nothing.
 *
 * So the injected session stops being the source of truth and becomes a
 * bootstrap: it gets the app past the screenshot-account gate, and from there
 * the app compares the session's email with the account the intent asks for. A
 * mismatch is repaired the only way that cannot lie — a real logout and a real
 * login — and the intent is put back in storage so the next boot replays this
 * slide, on the right account, through the exact same path as any other.
 *
 * The gate above still holds: a session that is not a screenshot account is
 * never touched, so this can never log a real user out.
 */
async function ensureScreenshotAccount(intent: ScreenshotIntent): Promise<boolean> {
  const account = SCREENSHOT_ACCOUNTS.find((a) => a.locale === intent.lang);
  if (!account) return false;

  const { data } = await supabase.auth.getUser();
  const current = data.user?.email?.trim().toLowerCase() ?? null;
  if (current === account.email.toLowerCase()) return false;
  if (current && !isScreenshotEmail(current)) return false;

  console.log(`[screenshot] account mismatch (${current ?? 'none'}), signing in as ${account.email}`);
  try {
    // logout() empties the account-scoped caches (clearAccountStorage), which
    // is exactly what has to go: households and children of the wrong account.
    await supabaseAuthService.logout();
    await supabaseAuthService.signInWithEmailPassword(account.email, SCREENSHOT_PASSWORD);
  } catch (err) {
    console.warn('[screenshot] account switch failed:', err);
    return false; // carry on rather than freeze: the runner will see the wrong capture
  }

  // Written AFTER the logout, which clears storage — the other order loses it.
  try { localStorage.setItem(INTENT_KEY, JSON.stringify(intent)); } catch (_) { /* noop */ }
  window.location.reload();
  return true;
}

/**
 * Wait until the household is loaded (currentHousehold set + at least one child),
 * which is what drives isPremium and the children list. Polls up to ~8s.
 */
async function waitForHouseholdReady(timeoutMs = 8000): Promise<void> {
  const appContext = useAppContextStore();
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (appContext.currentHousehold && appContext.availableChildren.length > 0) return;
    await new Promise(r => setTimeout(r, 200));
  }
}

/** The two celebrations the plan can showcase, and the RPC that stages each. */
const CELEBRATIONS = {
  chest: { type: 'chest_available', rpc: 'screenshot_trigger_chest_notification' },
  reward: { type: 'reward_unlocked', rpc: 'screenshot_trigger_reward_notification' },
} as const;

/**
 * Fire a celebration notification (same as the dev panel's 📦 / 🎁 buttons) so
 * its modal appears over the current screen. Both RPCs are SECURITY DEFINER and
 * refuse any caller that is not a screenshot-* account; the check below is the
 * client-side half of that pair. Targets the first available child.
 */
async function triggerCelebration(kind: keyof typeof CELEBRATIONS): Promise<void> {
  const { data } = await supabase.auth.getUser();
  if (!isScreenshotEmail(data.user?.email)) return;

  const appContext = useAppContextStore();
  const child = appContext.currentChild ?? appContext.availableChildren[0];
  if (!child) return;

  // The notification queue only watches the *active* child, so make sure it's
  // selected before we insert — otherwise the modal never surfaces.
  if (!appContext.currentChild) appContext.setCurrentChild(child as any);

  const { type, rpc } = CELEBRATIONS[kind];
  try {
    // Allow only THIS celebration through the suppression gate, so a stale
    // level_up or chest pending in the DB can't take its place.
    allowNextNotification(type);
    await (supabase as any).rpc(rpc, { p_child_id: child.id });

    // Drive the queue directly instead of relying on the 'app-resumed' listener
    // (which needs RewardNotificationQueue mounted — flaky right after a boot).
    // fetchPending() queries the DB and enqueues, surfacing the modal. Poll
    // until currentNotification is set so the capture only runs once it's up.
    const queue = useNotificationQueue();
    const started = Date.now();
    while (Date.now() - started < 8000) {
      await queue.fetchPending(child.id);
      if (queue.currentNotification.value) return; // modal is up
      await new Promise(r => setTimeout(r, 600));
    }
  } catch (err) {
    console.warn(`[screenshot] triggerCelebration(${kind}) failed:`, err);
  }
}

/**
 * Wait until the Habitudes page has its content, WITHOUT scrolling.
 *
 * The capture frames the page from the very top: the hero band, the day-of-week
 * chart, the streak trail, then the beginning of the anchoring list. An earlier
 * version scrolled the list up under the top bar to show more of it, and that
 * is gone — a store screenshot that opens mid-page reads as a cropped section,
 * and the sections above are the ones that say what the tab measures.
 *
 * What survives is the wait. The list carries `data-testid="habits-list"` and
 * only mounts once the 180 days of history have landed, so its appearance is
 * the honest signal that the page is done: without it the capture can catch the
 * skeleton, which the runner's stable-frame check accepts as a still image.
 */
async function waitForHabitsContent(): Promise<void> {
  const deadline = Date.now() + 12_000;
  while (Date.now() < deadline) {
    if (document.querySelector('[data-testid="habits-list"]')) return;
    await new Promise(r => setTimeout(r, 400));
  }
  console.warn('[screenshot] habits list never appeared — capturing the page as it stands');
}

/**
 * Normalize the showcased child's reward path for the Rewards capture: points
 * back to 120 (focal station 150 reads "30 points away") and stray collections
 * of stations >= 150 pts voided. Fixtures are seeded by
 * scripts/screenshots-fixtures.ts; this only undoes capture-session drift.
 * Double-guarded to screenshot-* accounts.
 */
async function normalizeRewardPath(child: { id: string; points?: number }): Promise<void> {
  const { data } = await supabase.auth.getUser();
  if (!isScreenshotEmail(data.user?.email)) return;
  try {
    if (child.points !== 120) {
      const { error } = await supabase.from('children').update({ points: 120 }).eq('id', child.id);
      if (!error) child.points = 120; // keep the store's reactive copy in sync
    }
    const { data: rewards } = await supabase.from('rewards').select('id,required_points');
    const bigIds = (rewards ?? []).filter((r) => (r.required_points ?? 0) >= 150).map((r) => r.id);
    if (bigIds.length > 0) {
      await supabase.from('collected_rewards').update({ status: 'void' })
        .eq('child_id', child.id).in('reward_id', bigIds).neq('status', 'void');
    }
  } catch (err) {
    console.warn('[screenshot] normalizeRewardPath failed:', err);
  }
}

/**
 * Load the allowance balance (household-wide) and the first child's movements
 * into the store so the Argent de poche panel renders populated. Read-only —
 * the fixtures themselves are seeded once in the DB for the screenshot accounts.
 * Double-guarded to screenshot-* accounts.
 */
async function preloadAllowance(childId: string): Promise<void> {
  const { data } = await supabase.auth.getUser();
  if (!isScreenshotEmail(data.user?.email)) return;
  try {
    const { useAllowance } = await import('@/composables/useAllowance');
    const { loadBalances, loadMovements } = useAllowance();
    await Promise.all([loadBalances(), loadMovements(childId)]);
  } catch (err) {
    console.warn('[screenshot] preloadAllowance failed:', err);
  }
}

/**
 * Complete recurring tasks for a child on a given date until at least `target`
 * are done (or all of them, whichever is smaller). Ticks random pending tasks.
 * Returns without writing if the target is already met. Assumes the caller has
 * already verified the screenshot account.
 */
async function completeRandomTasksForDate(childId: string, date: string, target: number): Promise<void> {
  const completion = await dailyCompletionService.getOrCreateDailyCompletion(childId, date);
  const tasks: Array<{ task_id?: string; id?: string; type?: string; status?: string }> =
    Array.isArray(completion?.tasks_data) ? completion.tasks_data : [];

  const recurring = tasks.filter((t) => t.type !== 'bonus');
  const completedCount = recurring.filter((t) => t.status === 'completed').length;
  if (completedCount >= target) return;

  const pending = recurring.filter((t) => t.status !== 'completed');
  // Shuffle (Fisher–Yates) so the ticked tasks vary between accounts/runs.
  for (let i = pending.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pending[i], pending[j]] = [pending[j], pending[i]];
  }

  for (const t of pending.slice(0, target - completedCount)) {
    const taskId = t.task_id || t.id;
    if (taskId) await dailyCompletionService.updateTaskStatus(childId, date, taskId, 'completed');
  }
}

/**
 * Ensure a child has at least `min` recurring tasks completed today, so the
 * Tasks screenshot reads as a real in-progress day. Idempotent and double-
 * guarded to screenshot-* accounts only.
 */
async function ensureMinCompletedTasks(childId: string, date: string, min: number): Promise<void> {
  // Belt-and-suspenders: never touch a real user's data.
  const { data } = await supabase.auth.getUser();
  if (!isScreenshotEmail(data.user?.email)) return;
  try {
    await completeRandomTasksForDate(childId, date, min);
  } catch (err) {
    console.warn('[screenshot] ensureMinCompletedTasks failed:', err);
  }
}
